import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DOC_TYPE_AR = {
  receipt: '🧾 وصل عملية يومية',
  invoice: '📄 فاتورة',
  installment_receipt: '📋 جدول أقساط',
  ticket: '🎫 تذكرة طيران',
  umrah_contract: '📜 عقد عمرة',
};
const EVENT_AR = {
  generated: { ar: '🟢 تم التوليد', class: 'bg-emerald-100 text-emerald-700' },
  downloaded: { ar: '⬇️ تم التحميل', class: 'bg-brand-100 text-brand-800' },
  printed: { ar: '🖨️ تم الطباعة', class: 'bg-blue-100 text-blue-700' },
  shared: { ar: '📤 تم المشاركة', class: 'bg-purple-100 text-purple-700' },
};

const DocumentCenter = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const qs = docType !== 'all' ? `?doc_type=${encodeURIComponent(docType)}&limit=500` : `?limit=500`;
      const r = await axios.get(`${API}/pdf-audit-logs${qs}`, auth());
      setRows(r.data.items || []);
    } catch (e) {
      toast.error('فشل تحميل السجلات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [docType]); // eslint will warn but `load` is stable inside this comp

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.user_email || '').toLowerCase().includes(q) ||
      (r.doc_id || '').toLowerCase().includes(q) ||
      (r.event || '').toLowerCase().includes(q) ||
      (r.doc_type || '').toLowerCase().includes(q)
    );
  });

  const verifyLink = (docId) => `${window.location.origin}/verify/${docId}`;

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl" data-testid="document-center-page">
      {/* Header */}
      <div className="bg-gradient-to-l from-noir-900 via-noir-800 to-noir-900 text-brand-400 p-6 rounded-2xl shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-wide">📋 مركز الوثائق</h1>
            <p className="text-brand-200 mt-1 text-sm">
              تتبّع كل ما يُولَّد ويُحمَّل ويُطبَع من PDFs — Audit Trail كامل
            </p>
          </div>
          <Button
            onClick={load}
            className="bg-brand-500 hover:bg-brand-600 text-noir-900 font-bold"
            data-testid="docs-refresh-btn"
          >
            🔄 تحديث
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-noir-600 mb-1 block">نوع الوثيقة</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="filter-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">📚 كل الأنواع</SelectItem>
                <SelectItem value="receipt">🧾 وصولات يومية</SelectItem>
                <SelectItem value="invoice">📄 فواتير</SelectItem>
                <SelectItem value="installment_receipt">📋 أقساط</SelectItem>
                <SelectItem value="ticket">🎫 تذاكر</SelectItem>
                <SelectItem value="umrah_contract">📜 عقود عمرة</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-noir-600 mb-1 block">بحث</label>
            <Input
              placeholder="إيميل الموظف، معرّف الوثيقة، نوع الحدث..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="filter-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'إجمالي العمليات', value: rows.length, color: 'from-brand-500 to-brand-600' },
          { label: 'تم التوليد', value: rows.filter((r) => r.event === 'generated').length, color: 'from-emerald-500 to-emerald-600' },
          { label: 'تم التحميل', value: rows.filter((r) => r.event === 'downloaded').length, color: 'from-blue-500 to-blue-600' },
          { label: 'تم الطباعة', value: rows.filter((r) => r.event === 'printed').length, color: 'from-purple-500 to-purple-600' },
        ].map((s) => (
          <div
            key={s.label}
            className={`bg-gradient-to-br ${s.color} text-white p-4 rounded-xl shadow text-center`}
            data-testid={`stat-${s.label}`}
          >
            <div className="text-3xl font-bold">{s.value}</div>
            <div className="text-xs opacity-90 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>السجل ({filtered.length} نتيجة)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-noir-500 py-10">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-noir-500 py-10">لا توجد سجلات</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">التاريخ</TableHead>
                    <TableHead className="text-right">الحدث</TableHead>
                    <TableHead className="text-right">نوع الوثيقة</TableHead>
                    <TableHead className="text-right">الموظف</TableHead>
                    <TableHead className="text-right">معرّف الوثيقة</TableHead>
                    <TableHead className="text-right">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r, idx) => {
                    const ev = EVENT_AR[r.event] || { ar: r.event, class: 'bg-noir-100 text-noir-700' };
                    const at = String(r.at || '').slice(0, 19).replace('T', ' ');
                    return (
                      <TableRow key={`${r.doc_id}-${idx}`}>
                        <TableCell className="font-mono text-xs whitespace-nowrap">{at}</TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${ev.class}`}>
                            {ev.ar}
                          </span>
                        </TableCell>
                        <TableCell>{DOC_TYPE_AR[r.doc_type] || r.doc_type}</TableCell>
                        <TableCell className="text-sm">{r.user_email || <span className="text-noir-400">—</span>}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-noir-600">{(r.doc_id || '').slice(0, 12)}...</span>
                        </TableCell>
                        <TableCell>
                          <a
                            href={verifyLink(r.doc_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-600 hover:text-brand-800 text-xs underline"
                            data-testid={`verify-link-${r.doc_id}`}
                          >
                            🔍 التحقق
                          </a>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentCenter;
